// Frontend mirror of the backend's purchase-edit-rules.ts at
// apps/webapp-ge/src/modules/service-packages/services/purchase-edit-rules.ts
// (webapp-back repo). Keep aligned — drift here causes the FE form to
// show "edit allowed" while the BE rejects (or vice versa).

export type EditState =
  | 'not_used'
  | 'partially_used'
  | 'fully_used'
  | 'expired'
  | 'terminal';

export interface PurchaseLike {
  status: 'ACTIVE' | 'USED' | 'CANCELLED' | 'EXPIRED';
  purchaseItems: Array<{ id: string; quantity: number }>;
  usages: Array<{
    purchaseItemId: string;
    quantityUsed: number;
    deletedAt: string | null;
  }>;
}

export function deriveEditState(purchase: PurchaseLike): EditState {
  if (purchase.status === 'CANCELLED') return 'terminal';
  // EXPIRED is not terminal: extending validUntil revives the purchase on the
  // backend. Only purchase-level fields open until then.
  if (purchase.status === 'EXPIRED') return 'expired';
  if (purchase.status === 'USED') return 'fully_used';

  const items = purchase.purchaseItems ?? [];
  if (items.length === 0) return 'not_used';

  const consumedByItem = new Map<string, number>();
  for (const u of purchase.usages ?? []) {
    if (u.deletedAt != null) continue;
    consumedByItem.set(
      u.purchaseItemId,
      (consumedByItem.get(u.purchaseItemId) ?? 0) + Number(u.quantityUsed ?? 0)
    );
  }

  const allFull = items.every(
    (i) => (consumedByItem.get(i.id) ?? 0) >= i.quantity
  );
  if (allFull) return 'fully_used';

  const anyUsed = items.some((i) => (consumedByItem.get(i.id) ?? 0) > 0);
  return anyUsed ? 'partially_used' : 'not_used';
}

export function consumedQtyFor(purchase: PurchaseLike, itemId: string): number {
  return (purchase.usages ?? [])
    .filter((u) => u.purchaseItemId === itemId && u.deletedAt == null)
    .reduce((sum, u) => sum + Number(u.quantityUsed ?? 0), 0);
}

export interface FieldEnablement {
  enabled: boolean;
  warning?: string;
  reason?: string;
}

export type EditableField =
  | 'displayName'
  | 'comment'
  | 'validUntil'
  | 'quantity'
  | 'serviceId'
  | 'originalPrice'
  | 'discountedPrice'
  | 'addItem'
  | 'removeItem';

// Shared price-edit warning (mirrors BE assertEditAllowed price warning).
const PRICE_WARNING =
  'მომხმარებელს უკვე გადახდილი აქვს; ფასის შეცვლა საჭიროებს დაანგარიშებას (დამატებით გადახდას ან თანხის დაბრუნებას)';

export function fieldEnablement(
  state: EditState,
  field: EditableField,
  itemConsumed = 0
): FieldEnablement {
  if (state === 'terminal') {
    return {
      enabled: false,
      reason: 'გაუქმებული პაკეტი ვერ რედაქტირდება',
    };
  }

  if (state === 'expired') {
    // Mirrors BE assertEditAllowed: term + labels only; items and prices wait
    // until the extended purchase is ACTIVE again.
    if (
      field === 'displayName' ||
      field === 'comment' ||
      field === 'validUntil'
    ) {
      return { enabled: true };
    }
    return { enabled: false, reason: 'ვადაგასულ პაკეტზე ჯერ ვადა გააგრძელეთ' };
  }

  if (state === 'not_used') {
    // Everything allowed in not_used. Mirrors BE assertEditAllowed.
    return { enabled: true };
  }

  if (state === 'fully_used') {
    // Nutsa #2231 — fields enabled on fully_used:
    // displayName, comment, validUntil, originalPrice (+warning),
    // discountedPrice (+warning), addItem.
    // Role gate for price on fully_used is applied in the COMPONENT (γ-9), not here.
    if (
      field === 'displayName' ||
      field === 'comment' ||
      field === 'validUntil'
    ) {
      return { enabled: true };
    }
    if (field === 'addItem') {
      return { enabled: true };
    }
    if (field === 'originalPrice' || field === 'discountedPrice') {
      return { enabled: true, warning: PRICE_WARNING };
    }
    // serviceId — explicit block per Nutsa #2231 (mirrors BE exact message).
    if (field === 'serviceId') {
      return {
        enabled: false,
        reason: 'სრულად გამოყენებულ პაკეტში სერვისის შეცვლა შეუძლებელია',
      };
    }
    // quantity — INCREASE allowed on fully_used (#2908 §8). The BE floor
    // (newQty >= consumed = total) rejects any reduction with its own message,
    // so we enable the field and let the BE enforce the lower bound.
    if (field === 'quantity') {
      return { enabled: true };
    }
    // removeItem — disabled on fully_used (service deletion blocked).
    return {
      enabled: false,
      reason: 'ამ მდგომარეობაში ვერ რედაქტირდება',
    };
  }

  // ── partially_used ──────────────────────────────────────────────────────
  // Purchase-level fields: displayName, comment, validUntil — all enabled
  // (Nutsa #2231: displayName/comment changed from disabled to enabled).
  if (
    field === 'displayName' ||
    field === 'comment' ||
    field === 'validUntil'
  ) {
    return { enabled: true };
  }

  if (field === 'addItem') {
    return { enabled: true };
  }

  // Item-level: serviceId + removeItem only allowed when item has 0 usages.
  if (field === 'serviceId' || field === 'removeItem') {
    if (itemConsumed === 0) return { enabled: true };
    return {
      enabled: false,
      reason: 'სერვისი გამოყენებულია; მისი წაშლა ან შეცვლა შეუძლებელია',
    };
  }

  if (field === 'quantity') {
    // Always editable on partially_used; the floor (≥consumed) is enforced
    // by the BE assertEditAllowed, not here.
    return { enabled: true };
  }

  if (field === 'originalPrice' || field === 'discountedPrice') {
    return { enabled: true, warning: PRICE_WARNING };
  }

  return { enabled: false };
}

export function reasonRequired(
  field: EditableField,
  state: EditState
): boolean {
  if (state === 'not_used' || state === 'expired' || state === 'terminal')
    return false;
  if (state === 'fully_used') return field === 'validUntil';
  // partially_used — #2908 §7: price edits no longer prompt for a reason.
  // The adjustment row is written server-side; the FE reason picker is removed.
  return false;
}

// Roles permitted to edit price on a fully_used package (Nutsa #2231 / mirrors
// BE assertPriceEditAllowedForRole — set(['Administrator','Admin','Manager'])).
export type RoleName = 'Administrator' | 'Admin' | 'Manager';

export function requiresRole(
  field: EditableField,
  state: EditState
): RoleName[] {
  if (
    state === 'fully_used' &&
    (field === 'originalPrice' || field === 'discountedPrice')
  ) {
    return ['Administrator', 'Admin', 'Manager'];
  }
  return [];
}
